import Phaser from "phaser";
import { MODULE_GRID, LAUNCHER } from "../data/balance.js";
import { FONT_FAMILY, hexToNum, RARITY_COLOR, TYPO } from "./uiTokens.js";
import { PALETTE, PALETTE_NUM } from "../themes/palette.js";
import type { ModuleGridController } from "../systems/ModuleGridController.js";
import type { PathResult } from "../modules/ModulePathFinder.js";
import { drawCellPorts, drawShapeLines, effectAbbr, portsFor } from "./shapeRenderer.js";
import { effectIconKey } from "../themes/imageKeys.js";
import { cellPortsForPlaced } from "../data/modulePorts.js";
import type { PlacedModule } from "../models/ModuleGrid.js";
import { isSingleFootprint, footprintCells, type CellOffset } from "../data/moduleFootprints.js";
import { unlockCostLabel } from "./formatNumber.js";
import type { BigNumber } from "@yjsgames/bignumber";
import type { ModuleRarity } from "../modules/ProjectileModule.js";
import { modulePresentationIconKey } from "./modulePresentationIcon.js";
import { ModuleGridProcFx, PROC_FX_MAX_PER_TICK, type BattleProcEvent } from "./ModuleGridProcFx.js";
import { computeFootprintBox, gridCellKey, gridPathCellSet } from "./moduleGridRenderMath.js";
import { FORCE_LOW_SPEC_RENDER_ENABLED } from "../env/appFlavor.js";
import {
  createModuleGridEmptyLayer,
  createModuleGridLockedLayer,
  drawModuleGridEmptyCell,
  drawModuleGridLockedCell,
  ensureModuleGridLockedCellTexture,
  type ModuleGridEmptyLayerCell,
  type ModuleGridLockedLayerCell,
} from "./moduleGridLockedLayer.js";
import { resolveSpaceshipLanePage } from "./spaceshipLanePager.js";

export type { BattleProcEvent, BattleProcKind } from "./ModuleGridProcFx.js";

export interface ModuleGridViewCallbacks {
  readonly onUnlock: (x: number, y: number, visibleLaneIndex: number) => void;
  readonly onMove: (fromX: number, fromY: number, toX: number, toY: number) => void;
  /** 배치 모듈 탭 — instanceId의 정보 팝업 열기 (모양 순환은 폐기된 기획). */
  readonly onModuleInfo: (instanceId: string) => void;
  /** 시작점(런처) 탭 — 런처 정보 팝업 열기 (단발 + 기본 치명타). */
  readonly onLauncherInfo: () => void;
  readonly onRemove: (x: number, y: number) => void;
  /** 다음 칸 해금 비용(BigNumber, 지수 누진) — 표시는 formatBig로 팝업과 통일. */
  readonly unlockCostBig: () => BigNumber;
}

/**
 * 셀 흐름 상태 — active(시안: Start와 연결되어 발동) / water(주황: 구 규칙 호환) /
 * blocked(빨강: 배치됐지만 물이 안 닿는 잘못된 파이프 — 포트 틀림·고립) / off(회색: 미방문 빈칸).
 */
// synergy(보라: 활성 시너지 조합 기여) / synergyFocus(금색: 모달 "파이프 표시"로 찍은 대상).
type CellState = "active" | "water" | "blocked" | "off" | "synergy" | "synergyFocus";

const STATE_ALPHA: Record<CellState, number> = { active: 0.66, water: 0.46, blocked: 0.64, off: 0.22, synergy: 0.72, synergyFocus: 0.86 };
const STATE_STROKE_W: Record<CellState, number> = { active: 2, water: 2, blocked: 2, off: 1, synergy: 3, synergyFocus: 5 };
const EMPTY_CELL_ALPHA: Record<CellState, number> = { active: 0.58, water: 0.5, blocked: 0.56, off: 0.66, synergy: 0.62, synergyFocus: 0.76 };
const EMPTY_CELL_STROKE_W: Record<CellState, number> = { active: 1, water: 1, blocked: 1, off: 1, synergy: 2, synergyFocus: 2 };
const EMPTY_SOCKET_RAIL_ALPHA: Record<CellState, number> = { active: 0.48, water: 0.42, blocked: 0.42, off: 0.58, synergy: 0.5, synergyFocus: 0.56 };

// 상태별 팔레트 토큰 키 (테마 무관 상수 — 색 자체는 PALETTE_NUM[key]로 호출 시점 조회해 테마 전환 안전).
const STATE_TOKEN = {
  cellBg: { active: "gridCellOnPath", water: "gridCellWater", blocked: "gridCellBlocked", off: "gridCellFilled", synergy: "gridCellSynergy", synergyFocus: "buttonActiveBg" },
  glow: { active: "gridPathGlow", water: "gridPathGlowWater", blocked: "gridPathGlowBlocked", off: "gridCellStroke", synergy: "gridPathGlowSynergy", synergyFocus: "selectionStroke" },
  lockStroke: { active: "gridPathGlow", water: "gridPathGlowWater", blocked: "gridPathGlowBlocked", off: "gridCellEmptyStroke", synergy: "gridPathGlowSynergy", synergyFocus: "selectionStroke" },
  port: { active: "portOn", water: "portWater", blocked: "portBlocked", off: "portOff", synergy: "portOn", synergyFocus: "accentGold" },
  portBg: { active: "portBgOn", water: "portBgWater", blocked: "portBgBlocked", off: "portBg", synergy: "portBgOn", synergyFocus: "buttonActiveBg" },
  tileStroke: { active: "gridPathGlow", water: "gridPathGlowWater", blocked: "gridPathGlowBlocked", off: "cellStroke", synergy: "gridPathGlowSynergy", synergyFocus: "accentGold" },
  // 모듈 아이콘/약어 tint — water(주황)/blocked(빨강)는 밝은 색으로 off(회색)와 구분(배경+회색 아이콘 가독성 함정 회피).
  iconTint: { active: "textBright", water: "portWater", blocked: "portBlocked", off: "textMuted", synergy: "textBright", synergyFocus: "accentGold" },
} as const;

function emptyFillColor(state: CellState): number {
  return state === "off" ? PALETTE_NUM.gridCellEmptyStroke : PALETTE_NUM[STATE_TOKEN.cellBg[state]];
}

function emptyLineColor(state: CellState): number {
  return state === "off" ? PALETTE_NUM.portOff : PALETTE_NUM[STATE_TOKEN.glow[state]];
}

const RARITY_BADGE_SIZE = 10;
const RARITY_BADGE_INSET = 7;

/** synergyInstanceIds 미지정(시너지 미발동/표시 불필요) 시의 빈 기본값 — 매 update 새 Set 생성 회피. */
const EMPTY_SYNERGY_SET: ReadonlySet<string> = new Set();
const EMPTY_RARITY_MAP: ReadonlyMap<string, ModuleRarity> = new Map();

function setVisibleActive<T extends Phaser.GameObjects.GameObject & { setVisible: (visible: boolean) => T }>(
  obj: T | undefined,
  visible: boolean,
): void {
  if (!obj) return;
  const managed = obj as Phaser.GameObjects.GameObject & {
    displayList?: unknown;
    addToDisplayList?: () => unknown;
    removeFromDisplayList?: () => unknown;
    setActive?: (value: boolean) => unknown;
  };
  if (visible && !managed.displayList) managed.addToDisplayList?.();
  obj.setVisible(visible);
  managed.setActive?.(visible);
  if (!visible) managed.removeFromDisplayList?.();
}

function setInputEnabled(obj: Phaser.GameObjects.GameObject & { input?: { enabled: boolean } | null }, enabled: boolean): void {
  if (obj.input) obj.input.enabled = enabled;
}

/**
 * 셀 슬롯의 표시 모드 — update가 매 갱신마다 이 모드로 전환한다(객체 destroy/recreate 대신 재사용).
 * locked(잠금) / empty(빈 해금칸) / module(배치 모듈 anchor) / endpoint(in-cell Start) / hidden(미사용·다중셀 비anchor).
 */
type CellMode = "hidden" | "empty" | "locked" | "module" | "endpoint";

/**
 * 그리드 한 좌표의 재사용 객체 묶음 — 생성자에서 1회 생성, update가 모드/속성만 갱신한다.
 * draggable bg + 드래그/제거/정보 핸들러도 1회만 등록(셀 좌표 캡처 + 모드/tile 동적 조회).
 */
interface CellSlot {
  /** 담당 그리드 좌표(고정). endpoint 전용 슬롯은 grid 밖 좌표를 담는다. */
  readonly x: number;
  readonly y: number;
  /** 클릭/드래그/정보 콜백 대상 — endpoint 슬롯은 Start(런처 정보) 전용으로 onLauncher만 발동. */
  readonly isEndpointSlot: boolean;
  readonly bg: Phaser.GameObjects.Rectangle;
  readonly g: Phaser.GameObjects.Graphics;
  readonly icon: Phaser.GameObjects.Image;
  rarityBadge?: Phaser.GameObjects.Rectangle;
  label?: Phaser.GameObjects.Text;
  lock?: Phaser.GameObjects.Image;
  cost?: Phaser.GameObjects.Text;
  mode: CellMode;
  tile: PlacedModule | null;
  /** Start endpoint 슬롯이면 true — pointerup에서 onLauncherInfo 분기. */
  launcher: boolean;
  /** label 그리기 모드 표시(아이콘 대신 텍스트 라벨 위치 보정용 dy). */
  labelDy: number;
  rarityDx: number;
  rarityDy: number;
  /** 직전 렌더 상태 fingerprint — 동일 상태 반복 update에서 Phaser setter/graphics redraw를 건너뛴다. */
  renderKey: string;
  dragged: boolean;
  removed: boolean;
  pressTimer: Phaser.Time.TimerEvent | null;
}

/**
 * 모듈 조립 그리드 렌더 + 드래그 이동/탭 정보팝업/우클릭·롱프레스 제거/잠금칸 클릭 해금 (지시문 §3·§16).
 * 팔레트→그리드 배치 hit-test는 pointerToCell로 BattleTabView가 처리.
 *
 * 셀 풀링(2026-06-14): 과거 update마다 전체 GameObject를 destroy/recreate했는데, draggable bg가
 * dragend 직후 destroy되면서 Phaser InputPlugin 내부 _list/_draggable에 dangling 참조가 누적
 * (#4463) → 장착/해제 반복 시 전역 pointer 콜백 비용이 단조 증가(전투탭 스크롤 떼는 순간 렉).
 * 이제 그리드 좌표마다 슬롯(bg/graphics/icon/rarityBadge/label/lock/cost)을 1회 생성·핸들러 1회 등록하고,
 * update는 모드 전환 + 속성 갱신만 한다(재생성 0 — ModuleGridView.test.ts 회귀 게이트).
 */
export class ModuleGridView {
  private readonly slots: CellSlot[] = [];
  /** grid 밖 endpoint 전용 슬롯. 런처만 표시하고 fire 슬롯은 호환용으로 숨긴다. */
  private readonly endpointSlots: CellSlot[] = [];
  private laneSignature = "";
  private lockedLayer?: Phaser.GameObjects.Image;
  private lockedLayerTexture?: Phaser.Textures.CanvasTexture;
  private lockedLayerRenderKey = "";
  private emptyLayer?: Phaser.GameObjects.Image;
  private emptyLayerTexture?: Phaser.Textures.CanvasTexture;
  private emptyLayerRenderKey = "";
  private built = false;
  private readonly procFx: ModuleGridProcFx;
  private readonly cell = MODULE_GRID.cellSize;
  private readonly step = MODULE_GRID.cellSize + MODULE_GRID.cellGap;
  private gw: number = MODULE_GRID.width;
  private gh: number = MODULE_GRID.height;
  private rowOffset = 0;
  private visibleRows: number = MODULE_GRID.visibleRows;
  private visibleLaneIndex = 0;
  private visible = true;
  private lastGrid: ModuleGridController | null = null;
  private lastPath: PathResult | null = null;
  private lastSynergyInstanceIds: ReadonlySet<string> = EMPTY_SYNERGY_SET;
  private lastRarityByInstanceId: ReadonlyMap<string, ModuleRarity> = EMPTY_RARITY_MAP;
  private lastFocusedSynergy = false;

  constructor(
    private readonly scene: Phaser.Scene,
    public originX: number,
    public originY: number,
    private readonly cb: ModuleGridViewCallbacks,
  ) {
    this.procFx = new ModuleGridProcFx(scene);
  }

  /** 그리드 원점 이동(펼치기/접기 재배치) — 저장 후 마지막 상태로 재렌더. 데이터 없으면 다음 update가 반영. */
  setOrigin(originX: number, originY: number): void {
    if (originX === this.originX && originY === this.originY) return;
    this.originX = originX;
    this.originY = originY;
    this.invalidateAllSlots();
    if (this.lastGrid && this.lastPath) {
      this.update(
        this.lastGrid,
        this.lastPath,
        this.lastSynergyInstanceIds,
        this.lastRarityByInstanceId,
        this.lastFocusedSynergy,
        this.visibleLaneIndex,
      );
    }
  }

  /** 그리드 픽셀 폭/높이 (BattleTabView 레이아웃용). */
  get pixelWidth(): number {
    return this.gw * this.step - MODULE_GRID.cellGap;
  }
  get pixelHeight(): number {
    return this.visibleRows * this.step - MODULE_GRID.cellGap;
  }

  private cellCenter(x: number, y: number): { cx: number; cy: number } {
    return {
      cx: this.originX + this.cell / 2 + x * this.step,
      cy: this.originY + this.cell / 2 + (y - this.rowOffset) * this.step,
    };
  }

  /** 게임 좌표(FIT 변환됨) → 셀 좌표. 그리드 밖이면 null. */
  pointerToCell(px: number, py: number): { x: number; y: number } | null {
    const gx = Math.floor((px - this.originX) / this.step);
    const localY = Math.floor((py - this.originY) / this.step);
    if (gx < 0 || localY < 0 || gx >= this.gw || localY >= this.visibleRows) return null;
    const gy = localY + this.rowOffset;
    if (gy < 0 || gy >= this.gh) return null;
    return { x: gx, y: gy };
  }

  // ── 슬롯 풀 생성(1회) ────────────────────────────────────

  /** 그리드 좌표 슬롯 + endpoint 슬롯을 1회 생성(객체 + 핸들러 1회 등록). */
  private buildSlots(grid: ModuleGridController): void {
    this.gw = grid.width;
    this.gh = grid.height;
    this.createGridLayers();
    for (let y = 0; y < this.gh; y++) {
      for (let x = 0; x < this.gw; x++) this.slots.push(this.createSlot(x, y, false));
    }
    // endpoint 슬롯은 grid 밖 좌표 담당(밖일 때만 visible). Start 슬롯은 런처 정보 클릭.
    this.endpointSlots.length = 0;
    const gridLanes = grid.lanes ?? [];
    const lanes = gridLanes.length > 0 ? gridLanes : [{ start: grid.start, fire: grid.fire }];
    lanes.forEach((lane) => {
      const startSlot = this.createSlot(lane.start.x, lane.start.y, true);
      startSlot.launcher = true;
      const fireSlot = this.createSlot(lane.fire.x, lane.fire.y, true);
      this.endpointSlots.push(startSlot, fireSlot);
    });
    this.built = true;
  }

  private destroySlots(): void {
    for (const slot of this.slots) this.destroySlot(slot);
    this.slots.length = 0;
    for (const slot of this.endpointSlots) this.destroySlot(slot);
    this.endpointSlots.length = 0;
    this.lockedLayer?.destroy();
    this.emptyLayer?.destroy();
    this.lockedLayer = undefined;
    this.emptyLayer = undefined;
    this.lockedLayerTexture = undefined;
    this.emptyLayerTexture = undefined;
    this.lockedLayerRenderKey = "";
    this.emptyLayerRenderKey = "";
    this.laneSignature = "";
    this.built = false;
  }

  private destroySlot(slot: CellSlot): void {
    slot.pressTimer?.remove();
    slot.bg.destroy();
    slot.g.destroy();
    slot.icon.destroy();
    slot.rarityBadge?.destroy();
    slot.label?.destroy();
    slot.lock?.destroy();
    slot.cost?.destroy();
  }

  private slotAt(x: number, y: number): CellSlot {
    return this.slots[y * this.gw + x]!;
  }

  /** 한 좌표의 재사용 객체 묶음 생성 + draggable 핸들러 1회 등록. */
  private createSlot(x: number, y: number, isEndpointSlot: boolean): CellSlot {
    const bg = this.scene.add
      .rectangle(0, 0, this.cell, this.cell, PALETTE_NUM.gridCellFilled)
      .setName("moduleGridBg")
      .setVisible(false)
      .setActive(false);
    const g = this.scene.add.graphics().setName("moduleGridGraphics").setVisible(false).setActive(false);
    const icon = this.scene.add
      .image(0, 0, "icon_grid_lock")
      .setName("moduleGridIcon")
      .setVisible(false)
      .setActive(false);
    const rarityBadge = FORCE_LOW_SPEC_RENDER_ENABLED
      ? undefined
      : this.scene.add
        .rectangle(0, 0, RARITY_BADGE_SIZE, RARITY_BADGE_SIZE, PALETTE_NUM.textBright)
        .setName("moduleGridRarityBadge")
        .setVisible(false)
        .setActive(false);
    const label = FORCE_LOW_SPEC_RENDER_ENABLED
      ? undefined
      : this.scene.add
        .text(0, 0, "", { fontFamily: FONT_FAMILY, fontSize: TYPO.tiny, color: PALETTE.textPrimary })
        .setName("moduleGridLabel")
        .setOrigin(0.5)
        .setVisible(false)
        .setActive(false);
    const lock = FORCE_LOW_SPEC_RENDER_ENABLED
      ? undefined
      : this.scene.add
        .image(0, 0, "icon_grid_lock")
        .setName("moduleGridLock")
        .setVisible(false)
        .setActive(false);
    const cost = FORCE_LOW_SPEC_RENDER_ENABLED
      ? undefined
      : this.scene.add
        .text(0, 0, "", { fontFamily: FONT_FAMILY, fontSize: TYPO.micro, color: PALETTE.accentGoldDim })
        .setName("moduleGridCost")
        .setOrigin(0.5)
        .setVisible(false)
        .setActive(false);

    const slot: CellSlot = {
      x, y, isEndpointSlot, bg, g, icon, rarityBadge, label, lock, cost,
      mode: "hidden", tile: null, launcher: false, labelDy: 0, rarityDx: 0, rarityDy: 0,
      renderKey: "", dragged: false, removed: false, pressTimer: null,
    };

    // bg = 드래그 핸들(모듈 모드만 실제 동작). 모든 셀 1회만 등록 → InputPlugin 재등록 누적 0.
    bg.setInteractive({ draggable: true, useHandCursor: true });
    setInputEnabled(bg, false);
    const moveAll = (px: number, py: number): void => {
      bg.setPosition(px, py);
      g.setPosition(px, py);
      icon.setPosition(px, py);
      slot.rarityBadge?.setPosition(px + slot.rarityDx, py + slot.rarityDy);
      slot.label?.setPosition(px, py + slot.labelDy);
    };
    bg.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      if (slot.mode !== "module") return;
      if (pointer.rightButtonDown()) {
        slot.removed = true;
        this.cb.onRemove(slot.x, slot.y);
        return;
      }
      slot.dragged = false;
      slot.removed = false;
      // 연속 pointerdown(빠른 탭/멀티터치) 시 이전 롱프레스 타이머가 살아있으면 취소 — 핸들러는 슬롯당
      // 1회 등록이라 remove 없이 재할당하면 stale 타이머가 onRemove를 늦게 발동시킬 수 있다.
      slot.pressTimer?.remove();
      slot.pressTimer = this.scene.time.delayedCall(450, () => {
        if (!slot.dragged) {
          slot.removed = true;
          this.cb.onRemove(slot.x, slot.y);
        }
      });
    });
    bg.on("drag", (_p: Phaser.Input.Pointer, dragX: number, dragY: number) => {
      if (slot.mode !== "module") return;
      slot.dragged = true;
      slot.pressTimer?.remove();
      bg.setDepth(60);
      g.setDepth(61);
      icon.setDepth(61);
      slot.rarityBadge?.setDepth(62);
      slot.label?.setDepth(61);
      moveAll(dragX, dragY);
    });
    bg.on("dragend", (pointer: Phaser.Input.Pointer) => {
      if (slot.mode !== "module") return;
      slot.pressTimer?.remove();
      if (slot.removed) return;
      const target = this.pointerToCell(pointer.x, pointer.y);
      if (target && (target.x !== slot.x || target.y !== slot.y)) this.cb.onMove(slot.x, slot.y, target.x, target.y);
      this.refresh();
    });
    bg.on("pointerup", () => {
      slot.pressTimer?.remove();
      if (slot.mode === "module") {
        if (slot.tile && !slot.dragged && !slot.removed) this.cb.onModuleInfo(slot.tile.instanceId);
      } else if (slot.mode === "locked") {
        this.cb.onUnlock(slot.x, slot.y, this.visibleLaneIndex);
      } else if (slot.launcher && slot.mode === "endpoint") {
        this.cb.onLauncherInfo();
      }
    });
    return slot;
  }

  private createGridLayers(): void {
    const width = Math.max(1, Math.floor(this.pixelWidth));
    const height = Math.max(1, Math.floor(this.pixelHeight));
    const lockedLayer = createModuleGridLockedLayer(this.scene, this.originX, this.originY, width, height);
    if (lockedLayer) {
      this.lockedLayerTexture = lockedLayer.texture;
      this.lockedLayer = lockedLayer.image;
    }
    const emptyLayer = createModuleGridEmptyLayer(this.scene, this.originX, this.originY, width, height);
    if (emptyLayer) {
      this.emptyLayerTexture = emptyLayer.texture;
      this.emptyLayer = emptyLayer.image;
    }
  }

  private syncLockedLayer(visible: boolean): void {
    if (!this.lockedLayer) return;
    this.lockedLayer.setPosition(this.originX, this.originY);
    setVisibleActive(this.lockedLayer, this.visible && visible);
  }

  private syncEmptyLayer(visible: boolean): void {
    if (!this.emptyLayer) return;
    this.emptyLayer.setPosition(this.originX, this.originY);
    setVisibleActive(this.emptyLayer, this.visible && visible);
  }

  private renderEmptyLayer(cells: readonly ModuleGridEmptyLayerCell[]): void {
    const texture = this.emptyLayerTexture;
    const layer = this.emptyLayer;
    if (!texture || !layer || cells.length === 0) {
      this.emptyLayerRenderKey = "";
      this.syncEmptyLayer(false);
      return;
    }
    const nextKey = cells
      .map((c) => `${c.x},${c.y},${c.stateKey},${c.fillColor},${c.strokeColor},${c.strokeWidth},${c.alpha},${c.railColor},${c.railAlpha}`)
      .join("|");
    if (this.emptyLayerRenderKey === nextKey) {
      this.syncEmptyLayer(true);
      return;
    }
    this.emptyLayerRenderKey = nextKey;
    const ctx = texture.getContext();
    const width = Math.max(1, Math.floor(this.pixelWidth));
    const height = Math.max(1, Math.floor(this.pixelHeight));
    ctx.clearRect(0, 0, width, height);
    for (const c of cells) {
      drawModuleGridEmptyCell(
        ctx,
        {
          ...c,
          x: c.x * this.step,
          y: c.y * this.step,
        },
        this.cell,
      );
    }
    texture.refresh();
    layer.setDisplaySize(width, height).setDepth(2);
    this.syncEmptyLayer(true);
  }

  private renderLockedLayer(cells: readonly ModuleGridLockedLayerCell[]): void {
    const texture = this.lockedLayerTexture;
    const layer = this.lockedLayer;
    const lockFrame = this.scene.textures?.getFrame?.("icon_grid_lock");
    if (!texture || !layer || !lockFrame || cells.length === 0) {
      this.lockedLayerRenderKey = "";
      this.syncLockedLayer(false);
      return;
    }
    const nextKey = cells.map((c) => `${c.x},${c.y},${c.stateKey},${c.canUnlock ? 1 : 0},${c.strokeColor}`).join("|");
    if (this.lockedLayerRenderKey === nextKey) {
      this.syncLockedLayer(true);
      return;
    }
    this.lockedLayerRenderKey = nextKey;
    const ctx = texture.getContext();
    const width = Math.max(1, Math.floor(this.pixelWidth));
    const height = Math.max(1, Math.floor(this.pixelHeight));
    ctx.clearRect(0, 0, width, height);
    for (const c of cells) {
      drawModuleGridLockedCell(
        ctx,
        lockFrame,
        c.x * this.step,
        c.y * this.step,
        this.cell,
        c.canUnlock,
        c.strokeColor,
      );
    }
    texture.refresh();
    layer.setDisplaySize(width, height).setDepth(2);
    this.syncLockedLayer(true);
  }

  // ── update(모드 전환 + 속성 갱신, 재생성 0) ───────────────

  update(
    grid: ModuleGridController,
    path: PathResult,
    synergyInstanceIds: ReadonlySet<string> = EMPTY_SYNERGY_SET,
    rarityByInstanceId: ReadonlyMap<string, ModuleRarity> = EMPTY_RARITY_MAP,
    focusedSynergy = false,
    visibleLaneIndex = 0,
  ): void {
    this.lastGrid = grid;
    this.lastPath = path;
    this.lastSynergyInstanceIds = synergyInstanceIds;
    this.lastRarityByInstanceId = rarityByInstanceId;
    this.lastFocusedSynergy = focusedSynergy;
    this.visibleLaneIndex = visibleLaneIndex;
    const gridLanes = (grid as unknown as { lanes?: readonly import("../models/ModuleGrid.js").ModuleGridLane[] }).lanes;
    const laneCount = Math.max(1, gridLanes?.length ?? 0);
    const lanePage = resolveSpaceshipLanePage(
      grid.height,
      gridLanes,
      visibleLaneIndex,
      laneCount > 1 ? MODULE_GRID.visibleRows : grid.height,
    );
    this.rowOffset = lanePage.rowOffset;
    this.visibleRows = lanePage.visibleRows;
    const nextLaneSignature = this.gridLaneSignature(grid);
    const nextWindowSignature = `${nextLaneSignature}#${this.rowOffset}:${this.visibleRows}`;
    if (!this.built || this.gw !== grid.width || this.gh !== grid.height || this.laneSignature !== nextWindowSignature) {
      if (this.built) this.destroySlots();
      this.laneSignature = nextWindowSignature;
      this.buildSlots(grid);
    }

    const activeSet = gridPathCellSet(this.gw, path.activeCells);
    const waterSet = gridPathCellSet(this.gw, path.waterOnlyCells);
    const blockedSet = gridPathCellSet(this.gw, path.blockedCells);
    const stateAt = (x: number, y: number): CellState =>
      activeSet.has(gridCellKey(this.gw, x, y))
        ? "active"
        : waterSet.has(gridCellKey(this.gw, x, y))
          ? "water"
          : blockedSet.has(gridCellKey(this.gw, x, y))
            ? "blocked"
            : "off";
    const isBlockedCell = (x: number, y: number): boolean =>
      typeof grid.isBlocked === "function" ? grid.isBlocked(x, y) : false;
    let unlockCostText: string | null = null;
    const useLockedLayer = Boolean(this.lockedLayer && this.lockedLayerTexture);
    const useEmptyLayer = Boolean(this.emptyLayer && this.emptyLayerTexture);
    const lockedLayerCells: ModuleGridLockedLayerCell[] = [];
    const emptyLayerCells: ModuleGridEmptyLayerCell[] = [];
    for (let y = 0; y < grid.height; y++) {
      for (let x = 0; x < grid.width; x++) {
        const slot = this.slotAt(x, y);
        if (y < this.rowOffset || y >= this.rowOffset + this.visibleRows) {
          this.hideSlot(slot);
          continue;
        }
        const state = stateAt(x, y);
        if (isBlockedCell(x, y)) {
          this.hideSlot(slot);
          continue;
        }
        if (!grid.isUnlocked(x, y)) {
          unlockCostText ??= unlockCostLabel(this.cb.unlockCostBig());
          const canUnlock = grid.canUnlock(x, y);
          if (useLockedLayer) lockedLayerCells.push({ x, y: y - this.rowOffset, stateKey: state, canUnlock, strokeColor: PALETTE_NUM[STATE_TOKEN.lockStroke[state]] });
          this.applyLocked(slot, state, unlockCostText, canUnlock, useLockedLayer);
          continue;
        }
        if (grid.isStart(x, y)) {
          if (!this.isVisibleLaneEndpoint(grid, x, y, "start", lanePage.pageIndex, lanePage.pageCount)) {
            this.hideSlot(slot);
            continue;
          }
          this.applyEndpointCell(slot, state, modulePresentationIconKey("launcher"), PALETTE_NUM.endpointStart, true);
          continue;
        }
        if (grid.isFire(x, y)) {
          if (!this.isVisibleLaneEndpoint(grid, x, y, "fire", lanePage.pageIndex, lanePage.pageCount)) {
            this.hideSlot(slot);
            continue;
          }
          this.hideSlot(slot);
          continue;
        }
        const cover = grid.moduleCoveringCell(x, y);
        if (cover) {
          if (cover.tile.x === x && cover.tile.y === y) {
            const cells = footprintCells(cover.tile.footprintId);
            // 시너지 기여 모듈은 active 위에 우선 — 활성 경로 안에서 보라로 별도 강조(유저가 시너지 조합 인지).
            const moduleState: CellState = synergyInstanceIds.has(cover.tile.instanceId)
              ? (focusedSynergy ? "synergyFocus" : "synergy")
              : cells.some((c) => activeSet.has(gridCellKey(this.gw, x + c.dx, y + c.dy)))
                ? "active"
                : cells.some((c) => waterSet.has(gridCellKey(this.gw, x + c.dx, y + c.dy)))
                  ? "water"
                  : cells.some((c) => blockedSet.has(gridCellKey(this.gw, x + c.dx, y + c.dy)))
                    ? "blocked"
                    : "off";
            this.applyModule(slot, x, y, cover.tile, moduleState, cells, rarityByInstanceId);
          } else {
            this.hideSlot(slot); // 다중셀 비anchor — anchor 슬롯의 확대 bg가 덮는다.
          }
          continue;
        }
        if (useEmptyLayer) {
          emptyLayerCells.push({
            x,
            y: y - this.rowOffset,
            stateKey: state,
            fillColor: emptyFillColor(state),
            strokeColor: emptyLineColor(state),
            strokeWidth: EMPTY_CELL_STROKE_W[state],
            alpha: EMPTY_CELL_ALPHA[state],
            railColor: emptyLineColor(state),
            railAlpha: EMPTY_SOCKET_RAIL_ALPHA[state],
          });
        }
        this.applyEmpty(slot, state, useEmptyLayer);
      }
    }
    this.renderEmptyLayer(emptyLayerCells);
    this.renderLockedLayer(lockedLayerCells);
    // grid 밖 endpoint 슬롯 — 런처만 표시한다. fire는 저장/데이터 호환 필드로만 남긴다.
    const endpointGridLanes = (grid as unknown as { lanes?: readonly import("../models/ModuleGrid.js").ModuleGridLane[] }).lanes;
    const endpointLanes = endpointGridLanes && endpointGridLanes.length > 0 ? endpointGridLanes : [{ start: grid.start, fire: grid.fire }];
    endpointLanes.forEach((lane, i) => {
      const startSlot = this.endpointSlots[i * 2];
      const fireSlot = this.endpointSlots[i * 2 + 1];
      if (lanePage.pageCount > 1 && i !== lanePage.pageIndex) {
        if (startSlot) this.hideSlot(startSlot);
        if (fireSlot) this.hideSlot(fireSlot);
        return;
      }
      if (startSlot) this.applyOutEndpoint(startSlot, grid, modulePresentationIconKey("launcher"), PALETTE_NUM.endpointStart, true, stateAt(lane.start.x, lane.start.y));
      if (fireSlot) this.hideSlot(fireSlot);
    });

    if (!this.visible) {
      this.forEachObject((o) => setVisibleActive(o, false));
      for (const slot of this.slots) this.syncSlotActivity(slot);
      for (const slot of this.endpointSlots) this.syncSlotActivity(slot);
    }
  }

  private gridLaneSignature(grid: ModuleGridController): string {
    const gridLanes = grid.lanes ?? [];
    const lanes = gridLanes.length > 0 ? gridLanes : [{ start: grid.start, fire: grid.fire }];
    return lanes.map((lane) => `${lane.start.x},${lane.start.y}>${lane.fire.x},${lane.fire.y}`).join("|");
  }

  private isVisibleLaneEndpoint(
    grid: ModuleGridController,
    x: number,
    y: number,
    endpoint: "start" | "fire",
    pageIndex: number,
    pageCount: number,
  ): boolean {
    if (pageCount <= 1) return true;
    const gridLanes = (grid as unknown as { lanes?: readonly import("../models/ModuleGrid.js").ModuleGridLane[] }).lanes;
    const lanes = gridLanes && gridLanes.length > 0 ? gridLanes : [{ start: grid.start, fire: grid.fire }];
    const lane = lanes[pageIndex];
    if (!lane) return false;
    const pos = endpoint === "start" ? lane.start : lane.fire;
    return pos.x === x && pos.y === y;
  }

  // ── 모드별 슬롯 갱신(객체 재사용) ─────────────────────────

  private invalidateAllSlots(): void {
    for (const slot of this.slots) slot.renderKey = "";
    for (const slot of this.endpointSlots) slot.renderKey = "";
    this.lockedLayerRenderKey = "";
    this.emptyLayerRenderKey = "";
  }

  private keepRendered(slot: CellSlot, key: string): boolean {
    if (slot.renderKey === key) return true;
    slot.renderKey = key;
    return false;
  }

  private syncSlotActivity(slot: CellSlot): void {
    const sync = (obj: Phaser.GameObjects.GameObject & Phaser.GameObjects.Components.Visible | undefined): void => {
      if (!obj) return;
      setVisibleActive(obj, this.visible && obj.visible);
    };
    sync(slot.bg);
    sync(slot.g);
    sync(slot.icon as unknown as Phaser.GameObjects.GameObject & Phaser.GameObjects.Components.Visible);
    sync(slot.rarityBadge);
    sync(slot.label);
    sync(slot.lock as unknown as Phaser.GameObjects.GameObject & Phaser.GameObjects.Components.Visible | undefined);
    sync(slot.cost);
    setInputEnabled(
      slot.bg,
      this.visible && (slot.mode === "module" || slot.mode === "locked" || (slot.mode === "endpoint" && slot.launcher)),
    );
  }

  /** 전부 숨김 — 다중셀 비anchor 셀. */
  private hideSlot(slot: CellSlot): void {
    if (this.keepRendered(slot, "hidden")) return;
    slot.mode = "hidden";
    slot.tile = null;
    slot.bg.setVisible(false);
    slot.g.setVisible(false);
    slot.icon.setVisible(false);
    slot.rarityBadge?.setVisible(false);
    slot.label?.setVisible(false);
    slot.lock?.setVisible(false);
    slot.cost?.setVisible(false);
    this.syncSlotActivity(slot);
  }

  private applyRarityBadge(
    slot: CellSlot,
    rarity: ModuleRarity,
    centerX: number,
    centerY: number,
    rectW: number,
    rectH: number,
  ): void {
    if (!slot.rarityBadge) return;
    const x = centerX + rectW / 2 - RARITY_BADGE_INSET;
    const y = centerY - rectH / 2 + RARITY_BADGE_INSET;
    slot.rarityDx = x - centerX;
    slot.rarityDy = y - centerY;
    slot.rarityBadge
      .setPosition(x, y)
      .setSize(RARITY_BADGE_SIZE, RARITY_BADGE_SIZE)
      .setRotation(Math.PI / 4)
      .setFillStyle(hexToNum(RARITY_COLOR[rarity] ?? RARITY_COLOR.common))
      .setStrokeStyle(1, PALETTE_NUM.borderSoft)
      .setAlpha(1)
      .setDepth(6)
      .setVisible(true);
    this.syncSlotActivity(slot);
  }

  private drawEmptySocketRails(slot: CellSlot, cx: number, cy: number, state: CellState): void {
    if (FORCE_LOW_SPEC_RENDER_ENABLED) {
      slot.g.setVisible(false);
      return;
    }
    const rail = this.cell / 2 - 7;
    const gap = Math.max(4, this.cell * 0.13);
    const color = emptyLineColor(state);
    const alpha = EMPTY_SOCKET_RAIL_ALPHA[state];
    slot.g
      .clear()
      .setPosition(cx, cy)
      .setAlpha(1)
      .setDepth(3)
      .setVisible(true);
    slot.g.lineStyle(1, color, alpha);
    slot.g.beginPath();
    slot.g.moveTo(-rail, 0);
    slot.g.lineTo(-gap, 0);
    slot.g.moveTo(gap, 0);
    slot.g.lineTo(rail, 0);
    slot.g.moveTo(0, -rail);
    slot.g.lineTo(0, -gap);
    slot.g.moveTo(0, gap);
    slot.g.lineTo(0, rail);
    slot.g.strokePath();
    slot.g.fillStyle(color, alpha * 0.75);
    slot.g.fillCircle(0, 0, 1.4);
  }

  /** 빈 해금 칸 — 기본 배경 셀만. */
  private applyEmpty(slot: CellSlot, state: CellState, useEmptyLayer = false): void {
    if (useEmptyLayer) {
      if (this.keepRendered(slot, `empty-layer|${state}`)) return;
      slot.mode = "empty";
      slot.tile = null;
      slot.bg.setVisible(false);
      slot.g.setVisible(false);
      slot.icon.setVisible(false);
      slot.rarityBadge?.setVisible(false);
      slot.label?.setVisible(false);
      slot.lock?.setVisible(false);
      slot.cost?.setVisible(false);
      this.syncSlotActivity(slot);
      return;
    }
    if (this.keepRendered(slot, `empty|${state}`)) return;
    slot.mode = "empty";
    slot.tile = null;
    const { cx, cy } = this.cellCenter(slot.x, slot.y);
    slot.bg
      .setPosition(cx, cy)
      .setSize(this.cell, this.cell)
      .setFillStyle(emptyFillColor(state))
      .setStrokeStyle(EMPTY_CELL_STROKE_W[state], emptyLineColor(state))
      .setAlpha(EMPTY_CELL_ALPHA[state])
      .setDepth(2)
      .setVisible(true);
    this.drawEmptySocketRails(slot, cx, cy, state);
    slot.icon.setVisible(false);
    slot.rarityBadge?.setVisible(false);
    slot.label?.setVisible(false);
    slot.lock?.setVisible(false);
    slot.cost?.setVisible(false);
    this.syncSlotActivity(slot);
  }

  /** 잠금 셀 — 어두운 배경 + 자물쇠 + 해금 비용. */
  private applyLocked(slot: CellSlot, state: CellState, unlockCostText: string, canUnlock: boolean, useLockedLayer: boolean): void {
    if (useLockedLayer) {
      if (this.keepRendered(slot, `locked-layer|${state}|${unlockCostText}|${canUnlock ? 1 : 0}`)) return;
      slot.mode = "locked";
      slot.tile = null;
      const { cx, cy } = this.cellCenter(slot.x, slot.y);
      // bg는 렌더하지 않는 투명 입력 영역으로만 유지하고, 잠김 셀 픽셀은 grid layer 한 장에 합성한다.
      slot.bg
        .setPosition(cx, cy)
        .setSize(this.cell, this.cell)
        .setFillStyle(PALETTE_NUM.gridCellEmpty)
        .setStrokeStyle(0, PALETTE_NUM[STATE_TOKEN.lockStroke[state]])
        .setAlpha(0.01)
        .setDepth(2)
        .setVisible(true);
      slot.g.setVisible(false);
      slot.icon.setVisible(false);
      slot.rarityBadge?.setVisible(false);
      slot.label?.setVisible(false);
      slot.lock?.setVisible(false);
      slot.cost
        ?.setPosition(cx, cy + 10)
        .setText(unlockCostText)
        .setColor(PALETTE.accentGoldDim)
        .setAlpha(canUnlock ? 0.96 : 0.5)
        .setDepth(4)
        .setVisible(true);
      this.syncSlotActivity(slot);
      return;
    }
    const lockedTexture = ensureModuleGridLockedCellTexture(
      this.scene,
      this.cell,
      state,
      canUnlock,
      PALETTE_NUM[STATE_TOKEN.lockStroke[state]],
    );
    if (this.keepRendered(slot, `locked|${state}|${unlockCostText}|${canUnlock ? 1 : 0}|${lockedTexture ?? "split"}`)) return;
    slot.mode = "locked";
    slot.tile = null;
    const { cx, cy } = this.cellCenter(slot.x, slot.y);
    if (lockedTexture) {
      // bg는 렌더하지 않는 투명 입력 영역으로만 유지하고, 배경+자물쇠는 한 텍스처로 합성한다.
      slot.bg
        .setPosition(cx, cy)
        .setSize(this.cell, this.cell)
        .setFillStyle(PALETTE_NUM.gridCellEmpty)
        .setStrokeStyle(0, PALETTE_NUM[STATE_TOKEN.lockStroke[state]])
        .setAlpha(0.01)
        .setDepth(2)
        .setVisible(true);
      slot.icon
        .setPosition(cx, cy)
        .setName("moduleGridLockedCell")
        .setTexture(lockedTexture)
        .setDisplaySize(this.cell, this.cell)
        .clearTint()
        .setAlpha(1)
        .setDepth(2)
        .setVisible(true);
      slot.lock?.setVisible(false);
      slot.cost
        ?.setPosition(cx, cy + 10)
        .setText(unlockCostText)
        .setColor(PALETTE.accentGoldDim)
        .setAlpha(canUnlock ? 0.96 : 0.5)
        .setDepth(4)
        .setVisible(true);
      slot.g.setVisible(false);
      slot.rarityBadge?.setVisible(false);
      slot.label?.setVisible(false);
      this.syncSlotActivity(slot);
      return;
    }
    slot.bg
      .setPosition(cx, cy)
      .setSize(this.cell, this.cell)
      .setFillStyle(PALETTE_NUM.gridCellEmpty)
      .setStrokeStyle(canUnlock ? 1 : 0, PALETTE_NUM[STATE_TOKEN.lockStroke[state]])
      .setAlpha(canUnlock ? 0.62 : 0.5)
      .setDepth(2)
      .setVisible(true);
    slot.lock
      ?.setPosition(cx, cy - 5)
      .setTexture("icon_grid_lock")
      .setDisplaySize(16, 16)
      .setTint(canUnlock ? PALETTE_NUM.textBright : PALETTE_NUM.textMuted)
      .setAlpha(canUnlock ? 0.82 : 0.42)
      .setDepth(3)
      .setVisible(true);
    slot.cost
      ?.setPosition(cx, cy + 10)
      .setText(unlockCostText)
      .setColor(PALETTE.accentGoldDim)
      .setAlpha(canUnlock ? 0.96 : 0.5)
      .setDepth(4)
      .setVisible(true);
    slot.g.setVisible(false);
    slot.icon.setVisible(false);
    slot.rarityBadge?.setVisible(false);
    slot.label?.setVisible(false);
    this.syncSlotActivity(slot);
  }

  /** in-cell Start — 기본 배경 + 엔드포인트 아이콘(흰 실루엣 + tint). */
  private applyEndpointCell(slot: CellSlot, state: CellState, iconKey: string, tint: number, launcher: boolean): void {
    if (this.keepRendered(slot, `endpoint-cell|${state}|${iconKey}|${tint}|${launcher ? 1 : 0}`)) return;
    slot.mode = "endpoint";
    slot.tile = null;
    slot.launcher = launcher; // in-cell Start면 런처 정보 클릭 활성(out 슬롯과 동일 정책)
    const { cx, cy } = this.cellCenter(slot.x, slot.y);
    const alpha = Math.min(0.62, STATE_ALPHA[state]);
    slot.bg
      .setPosition(cx, cy)
      .setSize(this.cell, this.cell)
      .setFillStyle(PALETTE_NUM[STATE_TOKEN.cellBg[state]])
      .setStrokeStyle(STATE_STROKE_W[state], PALETTE_NUM[STATE_TOKEN.glow[state]])
      .setAlpha(alpha)
      .setDepth(2)
      .setVisible(true);
    slot.icon.setName("moduleGridEndpointIcon").setPosition(cx, cy).setTexture(iconKey).setDisplaySize(24, 24).setTint(tint).setAlpha(alpha).setDepth(3).setVisible(true);
    slot.rarityBadge?.setVisible(false);
    slot.g.setVisible(false);
    slot.label?.setVisible(false);
    slot.lock?.setVisible(false);
    slot.cost?.setVisible(false);
    this.syncSlotActivity(slot);
  }

  /** grid 밖 런처 endpoint 슬롯 — 범위 안이면 숨김(셀 슬롯이 그림). */
  private applyOutEndpoint(
    slot: CellSlot,
    grid: ModuleGridController,
    iconKey: string,
    tint: number,
    isStart: boolean,
    state: CellState,
  ): void {
    if (!isStart || grid.inBounds(slot.x, slot.y)) {
      this.hideSlot(slot);
      slot.launcher = isStart; // 숨김이어도 정책 일관(클릭 불가 상태)
      return;
    }
    if (this.keepRendered(slot, `endpoint-out|${state}|${iconKey}|${tint}|${isStart ? 1 : 0}`)) return;
    slot.mode = "endpoint";
    slot.launcher = isStart;
    const { cx, cy } = this.cellCenter(slot.x, slot.y);
    const alpha = Math.min(0.62, STATE_ALPHA[state]);
    slot.bg
      .setPosition(cx, cy)
      .setSize(this.cell, this.cell)
      .setFillStyle(PALETTE_NUM[STATE_TOKEN.cellBg[state]])
      .setStrokeStyle(STATE_STROKE_W[state], PALETTE_NUM[STATE_TOKEN.glow[state]])
      .setAlpha(alpha)
      .setDepth(2)
      .setVisible(true);
    // 런처 — 모양 파이프 레일 + 시작 아이콘 + 구석 "S" 라벨.
    slot.g.clear().setPosition(cx, cy).setAlpha(alpha).setDepth(3).setVisible(true);
    drawShapeLines(slot.g, portsFor(LAUNCHER.shapeId), this.cell / 2 - 6, PALETTE_NUM[STATE_TOKEN.port[state]], 1);
    slot.icon.setName("moduleGridEndpointIcon").setPosition(cx, cy).setTexture(iconKey).setDisplaySize(20, 20).setTint(tint).setAlpha(alpha).setDepth(4).setVisible(true);
    slot.labelDy = 0;
    slot.label
      ?.setPosition(cx - this.cell / 2 + 3, cy - this.cell / 2 + 2)
      .setOrigin(0, 0)
      .setText("S")
      .setColor(PALETTE.textBright)
      .setAlpha(alpha)
      .setDepth(5)
      .setVisible(true);
    slot.rarityBadge?.setVisible(false);
    slot.lock?.setVisible(false);
    slot.cost?.setVisible(false);
    this.syncSlotActivity(slot);
  }

  /** 배치 모듈 1개 갱신 (anchor 슬롯, footprint 전체를 확대 bg로). 드래그/제거 핸들은 슬롯 1회 등록분 재사용. */
  private applyModule(
    slot: CellSlot,
    x: number,
    y: number,
    tile: PlacedModule,
    state: CellState,
    cells: readonly CellOffset[],
    rarityByInstanceId: ReadonlyMap<string, ModuleRarity>,
  ): void {
    const rarity = rarityByInstanceId.get(tile.instanceId) ?? "common";
    const iconKey = effectIconKey(tile.definitionId);
    if (
      this.keepRendered(
        slot,
        `module|${x}|${y}|${tile.instanceId}|${tile.definitionId}|${tile.shapeId}|${tile.footprintId}|${state}|${rarity}|${iconKey ?? ""}`,
      )
    ) {
      return;
    }
    slot.mode = "module";
    slot.tile = tile;
    slot.launcher = false;
    slot.dragged = false;
    slot.removed = false;
    const half = this.cell / 2;
    const alpha = STATE_ALPHA[state];
    const iconColor = STATE_TOKEN.iconTint[state];
    const portColor = PALETTE_NUM[STATE_TOKEN.port[state]];
    const { centerX, centerY, rectW, rectH } = computeFootprintBox(x, y, cells, (cx, cy) => this.cellCenter(cx, cy), this.step);
    slot.labelDy = iconKey ? 0 : rectH / 2 - 9;

    slot.bg
      .setPosition(centerX, centerY)
      .setSize(rectW, rectH)
      .setFillStyle(PALETTE_NUM[STATE_TOKEN.portBg[state]])
      .setStrokeStyle(STATE_STROKE_W[state], PALETTE_NUM[STATE_TOKEN.tileStroke[state]])
      .setAlpha(alpha)
      .setDepth(4)
      .setVisible(true);

    slot.g.clear().setPosition(centerX, centerY).setAlpha(alpha).setDepth(5).setVisible(true);
    if (isSingleFootprint(tile.footprintId)) {
      drawShapeLines(slot.g, portsFor(tile.shapeId), half - 6, portColor, 1);
    } else {
      const cellPorts = cellPortsForPlaced(tile.shapeId, tile.footprintId);
      const segs = cells.map((c, i) => {
        const cc = this.cellCenter(x + c.dx, y + c.dy);
        return { ox: cc.cx - centerX, oy: cc.cy - centerY, ports: cellPorts[i] ?? [] };
      });
      drawCellPorts(slot.g, segs, half - 6, portColor, 1);
    }

    if (iconKey) {
      slot.icon
        .setName("moduleGridModuleIcon")
        .setPosition(centerX, centerY)
        .setTexture(iconKey)
        .setDisplaySize(22, 22)
        .setTint(PALETTE_NUM[iconColor])
        .setAlpha(alpha)
        .setDepth(5)
        .setVisible(true);
      slot.label?.setVisible(false);
    } else if (slot.label) {
      slot.label
        .setPosition(centerX, centerY + slot.labelDy)
        .setOrigin(0.5)
        .setText(effectAbbr(tile.definitionId))
        .setColor(PALETTE[iconColor])
        .setAlpha(alpha)
        .setDepth(5)
        .setVisible(true);
      slot.icon.setVisible(false);
    } else {
      slot.icon.setVisible(false);
    }
    this.applyRarityBadge(slot, rarity, centerX, centerY, rectW, rectH);
    slot.lock?.setVisible(false);
    slot.cost?.setVisible(false);
    this.syncSlotActivity(slot);
  }

  /** 모든 슬롯 객체에 fn 적용(가시성 일괄 토글 등). */
  private forEachObject(fn: (o: Phaser.GameObjects.GameObject & Phaser.GameObjects.Components.Visible) => void): void {
    const all = (slot: CellSlot): void => {
      fn(slot.bg);
      fn(slot.g);
      fn(slot.icon as unknown as Phaser.GameObjects.GameObject & Phaser.GameObjects.Components.Visible);
      if (slot.rarityBadge) fn(slot.rarityBadge);
      if (slot.label) fn(slot.label);
      if (slot.lock) fn(slot.lock as unknown as Phaser.GameObjects.GameObject & Phaser.GameObjects.Components.Visible);
      if (slot.cost) fn(slot.cost);
    };
    if (this.lockedLayer) fn(this.lockedLayer as unknown as Phaser.GameObjects.GameObject & Phaser.GameObjects.Components.Visible);
    if (this.emptyLayer) fn(this.emptyLayer as unknown as Phaser.GameObjects.GameObject & Phaser.GameObjects.Components.Visible);
    for (const s of this.slots) all(s);
    for (const s of this.endpointSlots) all(s);
  }

  /** DnD 실패 시 스냅백 — 마지막 상태로 재렌더(슬롯 위치/depth 원복). */
  refresh(): void {
    if (this.lastGrid && this.lastPath) {
      this.invalidateAllSlots();
      this.update(
        this.lastGrid,
        this.lastPath,
        this.lastSynergyInstanceIds,
        this.lastRarityByInstanceId,
        this.lastFocusedSynergy,
        this.visibleLaneIndex,
      );
    }
  }

  /** 언어 변경 — 잠금 비용·효과 약어 등 t() 텍스트 갱신을 위해 마지막 상태로 1회 재렌더(객체 재사용). */
  applyLanguage(): void {
    this.refresh();
  }

  /**
   * 모듈/시작 파이프 발동 연출 — 발동 셀에 "발동!" 떠오름 + 테두리 펄스. 발동률 100%(chance 없는) 모듈도
   * 매 발사 발동하므로 표시 대상. startFired면 시작 파이프(Start endpoint)에도 1회 표시(발사가 일어난 곳).
   */
  showProc(procs: readonly BattleProcEvent[], startFired = false): void {
    if (!this.lastGrid || !this.visible) return;
    const nowMs = this.procFx.beginTick();
    let emitted = 0;
    for (const proc of procs) {
      if (emitted >= PROC_FX_MAX_PER_TICK) break;
      const tile = this.lastGrid.placementOf(proc.instanceId);
      if (!tile) continue;
      if (!this.procFx.canEmit(`module:${proc.instanceId}:${proc.kind}`, nowMs)) continue;
      const cells = footprintCells(tile.footprintId);
      const { centerX, centerY, rectW, rectH } = computeFootprintBox(
        tile.x,
        tile.y,
        cells,
        (x, y) => this.cellCenter(x, y),
        this.step,
      );
      this.procFx.emit(centerX, centerY, rectW, rectH, proc.kind);
      emitted += 1;
    }
    // 시작 파이프 — instanceId 없는 endpoint라 별도 처리. 다중 배선 우주선은 각 Start에 표시한다.
    if (startFired) {
      for (const slot of this.endpointSlots) {
        if (!slot.launcher || slot.mode !== "endpoint" || emitted >= PROC_FX_MAX_PER_TICK) continue;
        if (!this.procFx.canEmit(`start:${slot.x},${slot.y}`, nowMs)) continue;
        const { cx, cy } = this.cellCenter(slot.x, slot.y);
        this.procFx.emit(cx, cy, this.cell, this.cell);
        emitted += 1;
      }
    }
  }

  setVisible(visible: boolean): void {
    this.visible = visible;
    if (visible) {
      // 재진입 — 마지막 상태로 재렌더해 모드별 가시성 정확히 복원(전체 visible(true)는 hidden 슬롯까지 켜므로 금지).
      this.invalidateAllSlots();
      if (this.lastGrid && this.lastPath) {
        this.update(
          this.lastGrid,
          this.lastPath,
          this.lastSynergyInstanceIds,
          this.lastRarityByInstanceId,
          this.lastFocusedSynergy,
          this.visibleLaneIndex,
        );
      }
    } else {
      this.forEachObject((o) => setVisibleActive(o, false));
      for (const slot of this.slots) this.syncSlotActivity(slot);
      for (const slot of this.endpointSlots) this.syncSlotActivity(slot);
    }
  }
}
